import { useComposedRefs } from "../../utils/use-composed-refs.js";
import { useLatestRef } from "../../utils/use-latest-ref.js";
import { renderElement } from "../../utils/use-render.js";
import { usePosterContext } from "./context.js";
import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from "react";
//#region src/ui/poster/image.tsx
/**
* Displays the poster image managed by `Poster.Root`.
*
* Renders an `img`, so `srcSet`, `sizes`, `loading`, and the rest of the native image attributes remain available.
* Leave the source off and the player's resolved poster fills it in. The image is decorative unless you supply `alt`.
*/
const PosterImage = forwardRef(function PosterImage(componentProps, forwardedRef) {
	const { render, className, style, ...elementProps } = componentProps;
	const { state, setImageLoadState } = usePosterContext();
	const { src: authoredSrc, srcSet: authoredSrcSet } = elementProps;
	const src = authoredSrc ?? (authoredSrcSet === void 0 ? state.src : "");
	const initialLoadState = !src && authoredSrcSet === void 0 ? "none" : "loading";
	const [request, setRequest] = useState(() => ({
		src,
		srcSet: authoredSrcSet,
		state: initialLoadState
	}));
	const didCheckInitialImageRef = useRef(false);
	const requested = useLatestRef({
		src,
		srcSet: authoredSrcSet
	});
	const matchesRequest = request.src === src && request.srcSet === authoredSrcSet;
	if (!matchesRequest) setRequest({
		src,
		srcSet: authoredSrcSet,
		state: initialLoadState
	});
	const loadState = matchesRequest ? request.state : initialLoadState;
	useLayoutEffect(() => {
		setImageLoadState(loadState);
		return () => setImageLoadState("none");
	}, [
		authoredSrcSet,
		loadState,
		setImageLoadState,
		src
	]);
	const checkInitialImage = useCallback((img) => {
		if (!img || didCheckInitialImageRef.current) return;
		didCheckInitialImageRef.current = true;
		const { src, srcSet } = requested.current;
		if (img.naturalWidth > 0) setRequest({
			src,
			srcSet,
			state: "loaded"
		});
		else if (img.complete && (img.getAttribute("src") || img.hasAttribute("srcset"))) setRequest({
			src,
			srcSet,
			state: "error"
		});
	}, [requested]);
	const ref = useComposedRefs(forwardedRef, checkInitialImage);
	const handleLoad = () => setRequest({
		src,
		srcSet: authoredSrcSet,
		state: "loaded"
	});
	const handleError = () => setRequest({
		src,
		srcSet: authoredSrcSet,
		state: "error"
	});
	return renderElement("img", {
		render,
		className,
		style
	}, {
		state,
		ref,
		props: [
			{ alt: "" },
			elementProps,
			{
				src: src || void 0,
				onLoad: handleLoad,
				onError: handleError
			}
		]
	});
});
//#endregion
export { PosterImage };

//# sourceMappingURL=image.js.map